<template>
  <div
    ref="counterSection"
    class="bg-gradient-to-br from-green-800 to-[#164364] py-16 md:py-24 text-white"
  >
    <div class="container mx-auto px-4">
      <div class="max-w-4xl mx-auto text-center mb-16">
        <h2 class="text-5xl md:text-6xl font-bold text-white tracking-tight">
          Agricultural Impact Metrics
        </h2>
        <p class="text-xl text-white/80 mt-4 max-w-xl mx-auto">
          Empowering farmers through innovative solutions and sustainable growth
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          v-for="(stat, key) in counters"
          :key="key"
          class="group"
          :class="{
            'opacity-0 translate-y-8': !isVisible,
            'opacity-100 translate-y-0 transition-all duration-1000': isVisible,
            'delay-200': key === 'marketConnections',
            'delay-300': key === 'farmerNetwork',
            'delay-400': key === 'communitiesServed',
          }"
        >
          <div
            class="bg-white/10 border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-2"
          >
            <div class="flex justify-between items-start mb-4">
              <div>
                <div class="text-5xl font-extrabold text-white leading-tight">
                  {{ stat.toFixed(1)
                  }}<span
                    v-if="key === 'farmerNetwork'"
                    class="text-4xl align-top ml-1"
                    >K+</span
                  >
                  <span
                    v-else-if="key === 'incomeIncrease'"
                    class="text-3xl align-top ml-1"
                    >%</span
                  >
                  <span v-else class="text-3xl align-top ml-1">+</span>
                </div>
                <h3 class="text-lg font-semibold text-white/80 mt-2">
                  {{ getStatLabel(key) }}
                </h3>
              </div>
              <div
                class="text-white/70 opacity-70 group-hover:opacity-100 transition-opacity duration-300"
              >
                <svg
                  v-if="key === 'farmerNetwork'"
                  class="w-10 h-10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <svg
                  v-else-if="key === 'marketConnections'"
                  class="w-10 h-10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <svg
                  v-else-if="key === 'incomeIncrease'"
                  class="w-10 h-10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                <svg
                  v-else
                  class="w-10 h-10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h0A2.5 2.5 0 0013 5.5V4.352M11 3.5V3m-8.5 8.5h2.75A2.25 2.25 0 0 0 7.5 9.5v0A2.25 2.25 0 0 1 9.75 7.25h1.5a2.25 2.25 0 0 0 2.25-2.25v0a2.25 2.25 0 0 1 2.25-2.25h1.5m-9 8.25h5.5M3 19.5h15a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2H3a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z"
                  />
                </svg>
              </div>
            </div>
            <div class="w-full bg-white/20 rounded-full h-1.5 mt-4">
              <div
                class="bg-gradient-to-r from-green-500 to-emerald-600 h-full rounded-full transition-all duration-1000"
                :style="{ width: `${calculateProgressWidth(key)}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AgriculturalImpactMetrics",
  data() {
    return {
      counters: {
        farmerNetwork: 0,
        marketConnections: 0,
        incomeIncrease: 0,
        communitiesServed: 0,
      },
      targets: {
        farmerNetwork: 10.5, // 10,500+ farmers
        marketConnections: 350, // 350+ market connections
        incomeIncrease: 45, // 45% income increase
        communitiesServed: 250, // 250+ communities
      },
      isVisible: false,
      hasAnimated: false,
    };
  },
  mounted() {
    this.setupIntersectionObserver();
  },
  methods: {
    setupIntersectionObserver() {
      const section = this.$refs.counterSection;
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAnimated) {
              this.isVisible = true;
              this.startCounting();
              this.hasAnimated = true;
            }
          });
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0.3,
        }
      );

      observer.observe(section);
    },
    startCounting() {
      const steps = 30;

      const animate = () => {
        let allComplete = true;
        Object.keys(this.targets).forEach((key) => {
          const target = this.targets[key];
          const increment = target / steps;

          if (this.counters[key] < target) {
            this.counters[key] = Math.min(
              parseFloat((this.counters[key] + increment).toFixed(1)),
              target
            );
            allComplete = false;
          }
        });

        if (!allComplete) {
          requestAnimationFrame(animate);
        }
      };

      animate();
    },
    getStatLabel(key) {
      const labels = {
        farmerNetwork: "Farmers Empowered",
        marketConnections: "Market Connections",
        incomeIncrease: "Income Increase",
        communitiesServed: "Communities Served",
      };
      return labels[key];
    },
    calculateProgressWidth(key) {
      const progressMap = {
        farmerNetwork: (this.counters[key] / 10.5) * 100,
        marketConnections: (this.counters[key] / 350) * 100,
        incomeIncrease: this.counters[key],
        communitiesServed: (this.counters[key] / 250) * 100,
      };
      return progressMap[key];
    },
  },
};
</script>
